import { useTranslation } from "react-i18next";
import { IDocument, IRevision } from "../@types/document";
import { useToast } from "./Toast";
import { useContext, useEffect, useState } from "react";
import { getRevisionFileExtension } from "../lib/utils";
import { AppContext } from "../context/AppContextProvider";
import { ActionTypes } from "../actions/actions";
import Loader from "./Loader";

interface IDownloadButtonProps {
    doc: IDocument;
    revision: IRevision;
}

export default function DownloadButton({ doc, revision }: IDownloadButtonProps) {
    const toast = useToast();
    const { t } = useTranslation();
    const { state, dispatch } = useContext(AppContext);

    // Initialize state from context values
    const [isError, setIsError] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [localRevision, setLocalRevision] = useState<IRevision>(revision);
    const isLastestPublishedRevisionPdf = getRevisionFileExtension(revision.filename) === "pdf";

    useEffect(() => {
        if (!localRevision.file) return;

        const updatedRevisions = doc.revisions.map((r) =>
            r.automaticRevisionNumber === revision.automaticRevisionNumber ? localRevision : r
        );

        dispatch({
            type: ActionTypes.SetSpecificDoc,
            payload: { ...doc, revisions: updatedRevisions }
        });
    }, [localRevision.file])

    const fetchFile = async (url: string): Promise<File> => {
        const response = await fetch(url);

        if (!response.ok) {
            throw new Error("Failed to download file");
        }

        const blob = await response.blob();
        const contentType = response.headers.get("Content-Type") || "application/octet-stream";

        const fetchedFile = new File([blob], revision.filename, {
            type: isLastestPublishedRevisionPdf ? "application/pdf" : contentType,
        });

        return fetchedFile;
    }

    const handleDownload = async () => {
        try {
            setDownloading(true);
            setIsError(false);

            const fileToDownload = localRevision.file ?? await fetchFile(revision.fileUrl);

            if (!localRevision.file) {
                setLocalRevision({ ...localRevision, file: fileToDownload })
            }

            // Create a blob URL
            const blob = new Blob([fileToDownload], { type: fileToDownload.type });
            const blobUrl = URL.createObjectURL(blob);

            // Create a temporary link element
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = revision.filename;
            link.style.display = 'none';

            // Force download attribute
            link.setAttribute('download', revision.filename);

            // Add to document and trigger download
            document.body.appendChild(link);
            link.click();

            // Cleanup
            document.body.removeChild(link);
            URL.revokeObjectURL(blobUrl);
        } catch (error) {
            setIsError(true);
            console.error(error);
            toast?.showError(t("error.download"));
        } finally {
            setDownloading(false);
        }
    };

    return (
        <button
            disabled={downloading}
            className={`h-[24px] ${downloading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'} flex items-center justify-center bg-white hover:bg-gray-100 border border-gray-300 shadow-[_0px_0.667px_1.333px_0px_rgba(16,24,40,0.05)] rounded-[6px] transition-background duration-200 px-[6px] gap-[6px]`}
            onClick={() => {
                handleDownload()
                if (isError) return;

                window.dataLayer.push({
                    event: 'document_download',
                    fileName: revision.filename,
                    documentName: doc.documentName,
                    documentId: doc._id,
                })
            }}
        >
            {downloading ?
                <Loader size={14} borderWidth={2} />
                :
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M11 7.66667V8.33333C11 9.26675 11 9.73346 10.8183 10.09C10.6586 10.4036 10.4036 10.6586 10.09 10.8183C9.73347 11 9.26675 11 8.33333 11H3.66667C2.73325 11 2.26654 11 1.91002 10.8183C1.59641 10.6586 1.34144 10.4036 1.18166 10.09C1 9.73346 1 9.26675 1 8.33333V7.66667M8.77778 4.88889L6 7.66667M6 7.66667L3.22222 4.88889M6 7.66667V1" stroke="#3D4A67" strokeWidth="1.11111" strokeLinecap="round" strokeLinejoin="round" />
                </svg>}
        </button>
    );
}